import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {BookOfShadowsGameConfig} from "../BookOfShadowsGameConfig";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Linear, TimelineLite, TimelineMax, TweenLite} from "gsap";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {BookOfShadows} from "../BookOfShadows";
import {BookOfShadowsGameModuleConfig} from "../BookOfShadowsGameModuleConfig";
import {SoundManager} from "../sound/SoundManager";

export class BookOfShadowsInactiveRowView extends BaseView {
    public static readonly ROW_SELECTOR_CLICKED: string = "bookOfShadowsInactiveRowView_RowSelectorClicked";

    private _gameConfig: BookOfShadowsGameConfig = BookOfShadowsGameConfig.instance;
    private _whiteOverlay: PIXI.Sprite;
    private _overlayItem: TimelineSprite;
    private _layer: ScalableLayer;
    private _resourceLoaded: boolean = false;
    private _initDataReceived: boolean = false;

    private _betHighlightAnim: TimelineSprite;
    private _rowSelection: TimelineSprite;
    private _selectedRows: number = 3;
    private readonly _defaultActiveRows: number = 3; // defaulting it to 3 since we always have 3 rows(active symbols in a reel)
    private _shadowsPushUp: TimelineSprite;
    private _scaleForRowSelection: number = 0.5;

    private _wasFeatureBuy: boolean = false;
    private _isRestore: boolean = false;
    private _inActiveRowOverlay: PIXI.Graphics;
    private _tapIconTl: TimelineMax;
    private newArrowIcon:PIXI.Container;



    constructor() {
        super();
    }

    /**
     * @description To react on game's parsed init response
     * @param value
     * @param isRestore
     * @param wasFeatureBuy
     */
    public onGameInit(value: number, isRestore: boolean, wasFeatureBuy: boolean): void {
        this._selectedRows = value;
        this._wasFeatureBuy = wasFeatureBuy;

        this._initDataReceived = true;
        this._isRestore = isRestore;
        this.onGameReady();
    }


    private setRowSelectionTextures(activeRowsCount:number){
        let textures: PIXI.Texture[] = GameResources.getTextures("clickTo" + activeRowsCount);
        (this._rowSelection as any)._textures = textures;
    }


    /**
     * @description To enable/disable inActive row overlay graphics item.
     * @param enable
     */
    public enableInActiveRowOverlay(enable: boolean): void {
        // if feature is not allowed
        if (enable && !BookOfShadowsGameConfig.instance.ACTIVE_ROWS_ALLOWED_JURISDICTION) {
            return;
        }

        if (this._inActiveRowOverlay) {
            this._inActiveRowOverlay.interactive = enable;
        }
    }

    /**
     * @description Adjusting the row picker as per FS intro.
     * @param position
     */
    public performRowPickForFSIntro(position: number[]): void {
        this.enableInActiveRowOverlay(false);
        this.showArrowButton(false);

        this._rowSelection.position.set(position[0] + this._rowSelection.width / 2, position[1]);

        const activeRows = BookOfShadowsGameConfig.instance.ACTIVE_ROWS;
        this.setRowSelectionTextures(activeRows)
        this._rowSelection.setFrame(this._rowSelection.totalFrames);
        this._rowSelection.show();

        this.enableRowSelection(!GameSetting.replayMode);
    }

    /**
     * @description When we entered into FS.
     */
    public enteredFS(): void {
        this._rowSelection.hide();
    }

    /**
     * @description To react when free spin is over.
     * @param val
     */
    public updateNumberOfVisibleRows(val: number, force:boolean = false): void {
        if (val !== BookOfShadowsGameConfig.instance.ACTIVE_ROWS || force == true) {
            this.doRevealOverlay(val, false);
        }
        BookOfShadowsGameConfig.instance.ACTIVE_ROWS = val;
        this.showArrowButton(false);
    }

    /**
     * @description Handler for click on the amount items on FS intro screen.
     * @param selectedIndex
     */
    public handleReplay(selectedIndex: string): void {
        let activeRowsCount: number = 0;
        this.enableRowSelection(false);

        if (selectedIndex) {
            if (selectedIndex === "1") {
                activeRowsCount = 3;
            } else if (selectedIndex === "2") {
                activeRowsCount = 4;
            } else if (selectedIndex === "3") {
                activeRowsCount = 5;
            }
        }


        if (activeRowsCount ==  BookOfShadowsGameConfig.instance.ACTIVE_ROWS){
            return;
        }
        BookOfShadowsGameConfig.instance.ACTIVE_ROWS = activeRowsCount;
        this.doRevealOverlay(activeRowsCount, false);

        this.setRowSelectionTextures(activeRowsCount)
        this._rowSelection.setFrame(0);


    }

    /**
     * @description To set the boosted bet.
     */
    public setBoostedBet(): void {
        // if feature is not allowed
        if (!BookOfShadowsGameConfig.instance.ACTIVE_ROWS_ALLOWED_JURISDICTION) {
            return;
        }
        BookOfShadows.setBoostedBet();

        this._betHighlightAnim.getAnimationAutoShowHide(true, true);
    }

    /**
     * @description To toggle row clicker items.
     * @param show
     */
    public showArrowButton(show: boolean): void {
        if (show && !BookOfShadowsGameConfig.instance.ACTIVE_ROWS_ALLOWED_JURISDICTION) {
            return;
        }

        if (this._inActiveRowOverlay) {
            this._inActiveRowOverlay.visible = show;
            this.newArrowIcon.visible = show;
        }

        if (show) {
            if (this._tapIconTl) {
                this._tapIconTl.restart();
            }
        } else {
            if (this._tapIconTl) {
                this._tapIconTl.pause();
            }
        }

        this.enableInActiveRowOverlay(show);
    }

    /**
     * @inheritDoc
     */
    protected initAnimations(): void {
        super.initAnimations && super.initAnimations();

        this._layer = StageManager.getLayer(this._gameConfig.LAYERS.INACTIVE_ROW_SHADOW.name);
        this._layer.addChild(this);

        this.createOverlay();
        this.createInActiveRowOverlay();
        this.createTapIconAndArrow();
        this.createRowSelection();
        this.createBetHighlightAnim();
        this.createShadowsPushUpAnimation();

        this._resourceLoaded = true;
        this.onGameReady();
    }

    /**
     * @inheritDoc
     * @param resizeData
     */
    protected onResize(resizeData: IResizeData): void {
        super.onResize && super.onResize(resizeData);

        this._betHighlightAnim.position.set(NolimitApplication.screenBounds.left + 90, NolimitApplication.screenBounds.bottom - 30);
    }

    /**
     * @description To enable/disable row selection button.
     * @param enable
     */
    private enableRowSelection(enable: boolean): void {
        this._rowSelection.interactive = enable;
    }

    /**
     * @description To reveal the overlay.
     * @param data
     * @param alpha
     * @param duration
     */
    private revelOverlay(data: number[], alpha: number, duration: number): void {
        const toHeight: number = data[0],
            toHeightWhite: number = data[1],
            toY: number = data[2] - 12;

        TweenLite.to(this._overlayItem, duration, {
            height: toHeight
        });
        TweenLite.to(this._whiteOverlay, duration, {
            height: toHeightWhite
        });

        TweenLite.to(this._overlayItem, duration, {
            y: toY
        });
        TweenLite.to(this._whiteOverlay, duration, {
            y: toY
        });

        TweenLite.to([this._whiteOverlay, this._overlayItem], duration, {alpha: alpha});
    }

    private onRowSelectionMainGame(){
      //  this.doCommonClickThings();

        BookOfShadowsGameModuleConfig.gameTutorial.abort();
        this.enableRowSelection(false);
        BookOfShadowsGameModuleConfig.reelLockController.exitedFS();
        BookOfShadowsGameModuleConfig.reelLockController.displayReelLockCosts(false);

        let rows = BookOfShadowsGameConfig.instance.ACTIVE_ROWS + 1;
        if (rows > 5) {
            rows = this._defaultActiveRows;
        }

        const featureName =  "BOOSTED_BET_" + rows+ "_ROWS";
        BookOfShadows.api.betFeatureController.setActiveBetFeature(featureName)

    }

    public updateOverlay(visibleRows:number, animated:boolean) {
        this.doRevealOverlay(visibleRows, animated);
    }

    /**
     * @description Click handler for rowSelector.
     */
    private onRowSelectionClick(): void {
         BookOfShadowsGameModuleConfig.gameTutorial.abort();
        this.enableRowSelection(false);




        let activeRowsCount = BookOfShadowsGameConfig.instance.ACTIVE_ROWS + 1;
        if (activeRowsCount > 5){
            activeRowsCount = 3;
        }


        BookOfShadowsGameConfig.instance.ACTIVE_ROWS = activeRowsCount;

        this.doRevealOverlay(activeRowsCount, true);
        const bounceTl: TimelineLite = new TimelineLite();

        this.setRowSelectionTextures(activeRowsCount)

        bounceTl.to(this._rowSelection.scale, 0.06, {
            x: this._scaleForRowSelection * 0.9,
            y: this._scaleForRowSelection * 0.9
        }).to(this._rowSelection.scale, 0.1, {x: this._scaleForRowSelection, y: this._scaleForRowSelection});

        this._rowSelection.getAnimation(undefined, undefined, () => this.enableRowSelection(true));

        EventHandler.dispatchEvent(new GameEvent(BookOfShadowsInactiveRowView.ROW_SELECTOR_CLICKED));

    }

    /**
     * @description To create the overlay for inactive rows.
     */
    private createOverlay(): void {
        let sprite: TimelineSprite,
            whiteGraphics: PIXI.Sprite;

        whiteGraphics = new PIXI.Sprite(GameResources.getTextures("Shadows_White")[0]);
        whiteGraphics.anchor.set(0.5);
        whiteGraphics.position.set(this._gameConfig.REEL_AREA_WIDTH / 2, (this._gameConfig.SYMBOL_HEIGHT + this._gameConfig.REEL_AREA_POS_Y - 12));
        whiteGraphics.blendMode = PIXI.BLEND_MODES.ADD;
        this.addChild(whiteGraphics);
        this._whiteOverlay = whiteGraphics;

        sprite = new TimelineSprite(GameResources.getTextures("reelBlockerShadow"), 15);
        sprite.position.set(this._gameConfig.REEL_AREA_WIDTH / 2, (this._gameConfig.SYMBOL_HEIGHT + this._gameConfig.REEL_AREA_POS_Y - 12));
        sprite.anchor.set(0.5);
        sprite.blendMode = PIXI.BLEND_MODES.MULTIPLY;
        sprite.playLoop();
        this.addChild(sprite);
        this._overlayItem = sprite;
    }

    /**
     * @description To create the rowSelector and make it behave like a button.
     */
    private createRowSelection(): void {
        let item: TimelineSprite = new TimelineSprite(GameResources.getTextures("clickTo" + this._selectedRows), 15);
        item.anchor.set(0.5);
        item.interactive = false;
        item.on("pointertap", () => this.onRowSelectionClick());
        item.cursor = "pointer";
        item.visible = false;
        item.scale.set(this._scaleForRowSelection);

        this._rowSelection = item;
        StageManager.getLayer(this._gameConfig.LAYERS.ROW_SELECTOR_BUTTON_FS.name).addChild(this._rowSelection);
    }

    /**
     * @description To create the inactive row overlay which will act as the clickable item and will always be of the same size.
     */
    private createInActiveRowOverlay(): void {
        let item: PIXI.Graphics = new PIXI.Graphics();
        item.beginFill(0xffffff, 0.001);
        item.drawRect(0, 65, BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH, (2 * (BookOfShadowsGameConfig.instance.SYMBOL_HEIGHT)) + 5);
        item.endFill();
        item.interactive = true;
        item.on("pointertap", () => this.onRowSelectionMainGame());
        item.cursor = "pointer";

        this.addChild(item);
        this._inActiveRowOverlay = item;
    }

    /**
     * @description To create the tap icon with arrow.
     */
    private createTapIconAndArrow(): void {
        const tapIcon = new PIXI.Sprite(GameResources.getTextures("tapIcon")[0]);
        tapIcon.anchor.set(0.5);

        const tapIconArrow = new PIXI.Sprite(GameResources.getTextures("whiteArrow")[0]);
        tapIconArrow.position.set(0,10)
        tapIconArrow.anchor.set(1,0.5);
        tapIconArrow.rotation = (6.28 * (3 / 4));

        this.newArrowIcon = new PIXI.Container();
        this.newArrowIcon.position.set(360,160);
        this.newArrowIcon.addChild(tapIcon);
        this.newArrowIcon.addChild(tapIconArrow);
        this.addChild(this.newArrowIcon);


        const duration: number = 0.4;
        this._tapIconTl = new TimelineMax({
            repeat: -1
        });
        this._tapIconTl.add(TweenLite.to(tapIcon.scale, duration, {x: 0.88, y: 0.88}), 0);
        this._tapIconTl.add(TweenLite.to(tapIconArrow, duration, {y: tapIconArrow.y - 6}), 0);
        this._tapIconTl.add(TweenLite.to(tapIcon.scale, duration, {x: 1, y: 1}), duration);
        this._tapIconTl.add(TweenLite.to(tapIconArrow, duration, {y: tapIconArrow.y}), duration);
    }

    /**
     * @description To create the bet highlight animation which will be played whenever rowSelector is clicked.
     */
    private createBetHighlightAnim(): void {
        let item: TimelineSprite = new TimelineSprite(GameResources.getTextures("betHighlight"));
        item.blendMode = PIXI.BLEND_MODES.ADD;
        item.anchor.set(0.5);
        item.visible = false;
        this.addChild(item);
        this._betHighlightAnim = item;
    }

    /**
     * @description To create the shadow push up animation, played when a row selection is made.
     */
    private createShadowsPushUpAnimation(): void {
        let item: TimelineSprite = new TimelineSprite(GameResources.getTextures("shadowsPushUp"));
        item.visible = false;
        item.anchor.set(0.5);
        item.blendMode = PIXI.BLEND_MODES.ADD;
        item.position.set(this._gameConfig.REEL_AREA_WIDTH / 2, (this._gameConfig.REEL_AREA_POS_Y + (this._gameConfig.SYMBOL_HEIGHT * 3) / 2));
        this.addChild(item);
        this._shadowsPushUp = item;
    }

    /**
     * @description To reveal the overlay when a rowSelector is clicked.
     * @param playShadowAnim
     */
    private doRevealOverlay(visibleRows:number, playShadowAnim: boolean): void {
        let paramsData: number[] = [],
            alpha: number = 1,
            duration = this._shadowsPushUp.totalDuration,
            arrowY: number = 0;

        let flip = false;

        this.enableInActiveRowOverlay(false);

        const rowIndex = visibleRows - 3;

        if (rowIndex === 0) {
            duration = 0.1;
            paramsData = [320, 228, (this._gameConfig.REEL_AREA_POS_Y + this._gameConfig.SYMBOL_HEIGHT)];
            arrowY = 160;
        }
        else if (rowIndex === 1) {
            if (playShadowAnim) {
                this._shadowsPushUp.y = (this._gameConfig.REEL_AREA_POS_Y + (this._gameConfig.SYMBOL_HEIGHT * 3) / 2) + 35;
                this._shadowsPushUp.getAnimationAutoShowHide(true, true);
            }
            paramsData = [160, 114, (this._gameConfig.REEL_AREA_POS_Y + this._gameConfig.SYMBOL_HEIGHT / 2)];
            arrowY = 110;
        }
        else if (rowIndex === 2) {
            if (playShadowAnim) {
                this._shadowsPushUp.y = (this._gameConfig.REEL_AREA_POS_Y + (this._gameConfig.SYMBOL_HEIGHT) / 2) + 35;
                this._shadowsPushUp.getAnimationAutoShowHide(true, true);
            }
            paramsData = [this._overlayItem.height * 0.15, this._whiteOverlay.height * 0.15, (this._gameConfig.REEL_AREA_POS_Y + 10)];
            alpha = 0.6;
            arrowY = 112;
            flip = true;
        }

        if (playShadowAnim){
            SoundManager.playShadowRowChangeTo(rowIndex);
        }

        this.fadeAndRepositionArrow(duration, arrowY, flip)

        this.revelOverlay(paramsData, alpha, duration);
    }

    private fadeAndRepositionArrow(duration:number, yPos:number, flip:boolean){

        const anim = new TimelineMax();

        anim.add(TweenLite.to(this.newArrowIcon,duration / 2, {alpha:0, ease:Linear.easeNone}));
        anim.add(()=>{
            this.newArrowIcon.rotation = flip ? Math.PI : 0;
            this.newArrowIcon.position.set(360,yPos);
        });
        anim.add(TweenLite.to(this.newArrowIcon,duration / 2, {alpha:1, ease:Linear.easeNone}));
        anim.add(()=>{
            this.enableInActiveRowOverlay(true);
        });
    }

    /**
     * @description Method to react when resources are loaded and game's init response is parsed.
     */
    private onGameReady(): void {
        if (this._resourceLoaded && this._initDataReceived) {
            if (this._selectedRows === 4) {
                this._overlayItem.height = 160;
                this._overlayItem.y = this._gameConfig.REEL_AREA_POS_Y + (this._gameConfig.SYMBOL_HEIGHT / 2) - 12;

                this._whiteOverlay.height = 114;
                this._whiteOverlay.y = this._gameConfig.REEL_AREA_POS_Y + (this._gameConfig.SYMBOL_HEIGHT / 2) - 12;

            } else if (this._selectedRows === 5) {
                this._overlayItem.height = (160 * 0.15);
                this._overlayItem.y = (this._gameConfig.REEL_AREA_POS_Y - 2);
                this._overlayItem.alpha = 0.6;

                this._whiteOverlay.height = (114 * 0.15);
                this._whiteOverlay.y = (this._gameConfig.REEL_AREA_POS_Y - 2);
                this._whiteOverlay.alpha = 0.6;
            }

            BookOfShadowsGameConfig.instance.ACTIVE_ROWS = this._selectedRows;

            // making row selector disabled just before the FS intro presentation.
            if (this._isRestore) {
                this.enableRowSelection(false);
                this.enableInActiveRowOverlay(false);
                this.showArrowButton(false);

                if (this._wasFeatureBuy) {
                    this._rowSelection.hide();

                    this._wasFeatureBuy = false;
                }
            }

            if (!BookOfShadowsGameConfig.instance.ACTIVE_ROWS_ALLOWED_JURISDICTION) {
                this.showArrowButton(false);

                return;
            }

            this._isRestore = false;
        }
    }
}
